/**
 * Family Handover Vault — Pillar 2 private organisation hub.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  FileText,
  Loader2,
  Lock,
  MessageSquare,
  Phone,
  Shield,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import AppFooter from '../components/AppFooter';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import HandoverVaultItemModal from '../components/vault/HandoverVaultItemModal';
import {
  CATEGORY_LABELS,
  HANDOVER_VAULT_CATEGORIES,
  HANDOVER_VAULT_DISCLAIMER,
  HANDOVER_VAULT_EMPTY,
  HANDOVER_VAULT_SAFETY,
  HANDOVER_VAULT_SECTION_ORDER,
  HANDOVER_VAULT_TAGLINE,
} from '../content/handoverVaultContent';
import { handoverVaultApi } from '../utils/handoverVaultApi';
import { trackProductEvent } from '../utils/productEvents';

const SECTION_ICONS = {
  important_contact: Phone,
  document_location: FileText,
  account_instruction: Shield,
  final_message: MessageSquare,
  notify_person: UserPlus,
  review_reminder: Bell,
};

function groupByCategory(items) {
  const groups = {};
  HANDOVER_VAULT_SECTION_ORDER.forEach((key) => {
    groups[key] = [];
  });
  items.forEach((item) => {
    const key = item.category;
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  return groups;
}

export default function HandoverVaultPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_category: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCategory, setModalCategory] = useState('important_contact');
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        handoverVaultApi.list(),
        handoverVaultApi.summary(),
      ]);
      setItems(Array.isArray(list) ? list : []);
      setSummary(sum || { total: 0, by_category: {} });
    } catch {
      setItems([]);
      setSummary({ total: 0, by_category: {} });
      toast.error('Could not load vault items. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    trackProductEvent('vault_opened', { pillar: 'vault' });
  }, []);

  useEffect(() => {
    const add = searchParams.get('add');
    if (add && HANDOVER_VAULT_CATEGORIES[add]) {
      openAddModal(add);
      const next = new URLSearchParams(searchParams);
      next.delete('add');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => groupByCategory(items), [items]);
  const filteredGroups = useMemo(() => {
    if (filterCategory === 'all') return grouped;
    return { [filterCategory]: grouped[filterCategory] || [] };
  }, [grouped, filterCategory]);

  const openAddModal = (category) => {
    setEditingItem(null);
    setModalCategory(category);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setModalCategory(item.category);
    setModalOpen(true);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editingItem) {
        await handoverVaultApi.update(editingItem.id, payload);
        toast.success('Vault item updated.');
      } else {
        await handoverVaultApi.create(payload);
        toast.success('Saved to your vault.');
      }
      setModalOpen(false);
      setEditingItem(null);
      await loadItems();
    } catch {
      toast.error('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (item) => {
    if (!window.confirm(`Remove "${item.title}" from your vault?`)) return;
    try {
      await handoverVaultApi.archive(item.id);
      toast.success('Item removed.');
      await loadItems();
    } catch {
      toast.error('Could not remove item.');
    }
  };

  const totalItems = summary.total ?? items.length;

  return (
    <div className="min-h-screen bg-[hsl(42_28%_97%)]" data-testid="handover-vault-page">
      <header className="border-b border-[hsl(38_16%_82%)] bg-[hsl(42_28%_99%)]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#5c534a] hover:text-[#1c1917]">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Dashboard
          </Link>
          <BrandWordmark className="h-6 opacity-80" />
          <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
            <Link to="/guide#family-handover-vault" className="text-xs text-[#7a7268] hover:text-[#5c534a]">
              Feature Guide
            </Link>
            <span className="hidden text-[#d4cdc3] sm:inline" aria-hidden>|</span>
            <Link to="/help#handover-vault" className="text-xs text-[#7a7268] hover:text-[#5c534a]">
              Vault guidance
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-12">
        <div className="mb-8 max-w-2xl">
          <p className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8b7355]">
            <Lock className="h-3.5 w-3.5" aria-hidden />
            Family Handover Vault · Pillar 2
          </p>
          <h1 className="font-serif text-3xl font-light text-[#1c1917] sm:text-4xl">
            Family Handover Vault
          </h1>
          <p className="mt-3 text-[0.95rem] leading-relaxed text-[#5c534a]">{HANDOVER_VAULT_TAGLINE}</p>
          <p className="mt-2 text-xs text-[#7a7268]">{HANDOVER_VAULT_SAFETY}</p>
        </div>

        <div
          className="mb-8 rounded-xl border border-[hsl(38_16%_82%)] bg-white/70 px-4 py-3 text-sm leading-relaxed text-[#5c534a]"
          role="note"
          data-testid="handover-vault-disclaimer"
        >
          <strong className="font-medium text-[#1c1917]">Please note:</strong> {HANDOVER_VAULT_DISCLAIMER}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#7a7268]">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden />
            Loading your vault…
          </div>
        ) : (
          <>
            <div className="mb-8 grid gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="handover-vault-summary">
              {HANDOVER_VAULT_SECTION_ORDER.map((key) => {
                const count = summary.by_category?.[key] ?? grouped[key]?.length ?? 0;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilterCategory(filterCategory === key ? 'all' : key)}
                    className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                      filterCategory === key
                        ? 'border-[#8B7355] bg-[hsl(42_28%_99%)]'
                        : 'border-[hsl(38_16%_82%)] bg-white/60 hover:bg-white/90'
                    }`}
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8b7355]">{count}</p>
                    <p className="mt-1 text-xs leading-snug text-[#5c534a]">{CATEGORY_LABELS[key]}</p>
                  </button>
                );
              })}
            </div>

            {filterCategory !== 'all' ? (
              <div className="mb-4">
                <Button type="button" size="sm" variant="ghost" className="rounded-full text-[#5c534a]" onClick={() => setFilterCategory('all')}>
                  Show all categories
                </Button>
              </div>
            ) : null}

            <div className="mb-10 grid gap-4 sm:grid-cols-2" data-testid="handover-vault-sections">
              {HANDOVER_VAULT_SECTION_ORDER.map((key) => {
                const section = HANDOVER_VAULT_CATEGORIES[key];
                const Icon = SECTION_ICONS[key] || FileText;
                return (
                  <article
                    key={key}
                    className="rounded-2xl border border-[hsl(38_16%_82%)]/90 bg-white/80 p-5 shadow-sm"
                    data-testid={`vault-section-${key}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(42_24%_94%)] text-[#8b7355]">
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                      <div className="min-w-0 flex-1">
                        <h2 className="font-serif text-lg font-light text-[#1c1917]">{section.label}</h2>
                        <p className="mt-1 text-sm leading-relaxed text-[#5c534a]">{section.copy}</p>
                        {section.examples ? (
                          <p className="mt-2 text-xs text-[#7a7268]">
                            e.g. {section.examples.slice(0, 4).join(', ')}
                            {section.examples.length > 4 ? '…' : ''}
                          </p>
                        ) : null}
                        {section.warning ? (
                          <p className="mt-2 text-xs leading-relaxed text-amber-900/90">{section.warning}</p>
                        ) : null}
                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            className="rounded-full bg-[#8B7355] hover:bg-[#7A6548]"
                            onClick={() => openAddModal(key)}
                            data-testid={`vault-add-${key}`}
                          >
                            {section.addLabel}
                          </Button>
                          {key === 'review_reminder' ? (
                            <Button size="sm" variant="outline" className="rounded-full" asChild>
                              <Link to="/reminders">Set review reminder</Link>
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {totalItems === 0 ? (
              <section
                className="rounded-2xl border border-dashed border-[hsl(38_16%_82%)] bg-[hsl(42_28%_99%)] px-6 py-10 text-center"
                data-testid="handover-vault-empty"
              >
                <h2 className="font-serif text-xl font-light text-[#1c1917]">{HANDOVER_VAULT_EMPTY.title}</h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-[#5c534a]">{HANDOVER_VAULT_EMPTY.copy}</p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {HANDOVER_VAULT_EMPTY.actions.map((action) => (
                    <Button
                      key={action.category}
                      type="button"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => openAddModal(action.category)}
                    >
                      {action.label}
                    </Button>
                  ))}
                </div>
              </section>
            ) : (
              <section className="space-y-8" data-testid="handover-vault-items">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-serif text-xl font-light text-[#1c1917]">Your vault items</h2>
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-full bg-[#8B7355] hover:bg-[#7A6548]"
                    onClick={() => openAddModal('important_contact')}
                    data-testid="handover-vault-add-item"
                  >
                    Add vault item
                  </Button>
                </div>

                {Object.entries(filteredGroups).map(([key, list]) => {
                  if (!list.length && filterCategory !== 'all') return null;
                  if (!list.length) return null;
                  return (
                    <div key={key}>
                      <h3 className="mb-3 text-sm font-medium text-[#5c534a]">{CATEGORY_LABELS[key]}</h3>
                      <ul className="space-y-2">
                        {list.map((item) => (
                          <li
                            key={item.id}
                            className="flex flex-col gap-2 rounded-xl border border-[hsl(38_16%_82%)] bg-white/90 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                            data-testid={`vault-item-${item.id}`}
                          >
                            <div className="min-w-0">
                              <p className="font-medium text-[#1c1917]">{item.title}</p>
                              {item.description ? (
                                <p className="mt-1 line-clamp-2 text-sm text-[#5c534a]">{item.description}</p>
                              ) : null}
                              {item.contact_name ? (
                                <p className="mt-1 text-xs text-[#7a7268]">
                                  {item.contact_name}
                                  {item.contact_phone ? ` · ${item.contact_phone}` : ''}
                                </p>
                              ) : null}
                              {item.document_location ? (
                                <p className="mt-1 text-xs text-[#7a7268]">Location: {item.document_location}</p>
                              ) : null}
                            </div>
                            <div className="flex shrink-0 gap-2">
                              <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => openEditModal(item)}>
                                Edit
                              </Button>
                              <Button type="button" size="sm" variant="ghost" className="rounded-full text-[#7a7268]" onClick={() => handleArchive(item)}>
                                Remove
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </section>
            )}
          </>
        )}
      </main>

      <AppFooter />

      <HandoverVaultItemModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        initialCategory={modalCategory}
        editingItem={editingItem}
        onSave={handleSave}
        saving={saving}
      />
    </div>
  );
}
